import api from './api';

export type YardEntryType = 'earn' | 'deliver';

/**
 * Settlement state of a '+1' earn (backend `_settlementByContainer`):
 *   settled   — the container's leg2 recorded its −1
 *   in_flight — leg2 not created / not completed yet: normal
 *   stranded  — leg2 completed but no −1 was recorded (through pricing did not
 *               apply), so the +1 stays open until someone runs direct-pay
 */
export type YardSettlement = 'settled' | 'in_flight' | 'stranded';

/**
 * One credit pool: a driver's balance at ONE yard. Pools are per yard because the
 * yards sit at different distances — credits earned at one can't settle a run out
 * of the other (backend migration 210). `colorIndex` is the yard's stable rank;
 * feed it to `yardColor()` so the same yard keeps the same colour everywhere.
 */
export interface YardCreditPool {
  yardId: number | null;
  yardName: string | null;
  yardFullName?: string | null;
  colorIndex: number | null;
  balance: number;
}

export interface YardLedgerEntry {
  id: number;
  company_id: number;
  driver_id: number;
  entry_type: YardEntryType;
  delta: number;
  task_id: number;
  container_id: number | null;
  payment_id: number | null;
  port_id: number | null;
  /** Which yard's pool this entry belongs to (pinned when it was written). */
  yard_id: number | null;
  created_at: string;
  // Enriched by list() for display
  task_business_id?: string | null;
  container_number?: string | null;
  driver_name?: string | null;
  task_driver_id?: number | null;
  origin_type?: string | null;
  destination_type?: string | null;
  origin_name?: string | null;
  dest_name?: string | null;
  /** Facility short names for the route column; may be null when none is on file. */
  origin_short_name?: string | null;
  dest_short_name?: string | null;
  yard_name?: string | null;
  yard_short_name?: string | null;
  yard_color_index?: number | null;
  // Settlement tracking — 'earn' rows only
  settlement?: YardSettlement;
  settlement_reason?: string | null;
  leg2_task_id?: number | null;
}

export interface YardTaskContext {
  taskId: number;
  containerId: number | null;
  driverId: number | null;
  skipYardCredit: boolean;
  isYardLeg1: boolean;
  isYardLeg2: boolean;
  /** The yard this leg's credit belongs to (null when it isn't a credit leg). */
  yardId: number | null;
  yardName: string | null;
  yardColorIndex: number | null;
  /** Balance of THIS yard's pool — the one that decides who should get the
   *  delivery leg out of this yard. Not the cross-yard sum. */
  balance: number;
  /** Roll-up across every yard. Display only. */
  totalBalance: number;
  settlement: YardSettlement | null;
  settlementReason: string | null;
  ledgerEntries: YardLedgerEntry[];
}

/**
 * Yard-storage credit ledger API (see backend migration 134 +
 * docs/YARD_STORAGE_CREDIT_DESIGN.md). The response interceptor already unwraps
 * `{ success, data }` → use `res.data`.
 */
export const driverYardCreditApi = {
  /** Yard context for a task — lets SalaryPanel render a held leg1 / a leg2 badge. */
  async getTaskContext(taskId: number): Promise<YardTaskContext | null> {
    const res = await api.get(`/driver-yard-credits/task/${taskId}/context`);
    return res.data;
  },

  /** Ledger rows (per driver / container). */
  async list(params?: { driverId?: number; containerId?: number }): Promise<YardLedgerEntry[]> {
    const res = await api.get('/driver-yard-credits', { params });
    return res.data || [];
  },

  /** One driver: the cross-yard total plus the per-yard pools behind it. */
  async getBalance(driverId: number): Promise<{ driverId: number; balance: number; byYard: YardCreditPool[] }> {
    const res = await api.get(`/driver-yard-credits/balance/${driverId}`);
    return res.data;
  },

  /**
   * Every driver's pools in one call, as a `driverId → pools[]` map, ordered by
   * the yard colour rank so every card lists the yards in the same order.
   * Drivers with no ledger activity are absent — read a missing key as no pools.
   * Use this instead of aggregating `list()` client-side when only the numbers
   * are needed; it skips the ledger's joins and settlement lookup.
   */
  async getBalances(): Promise<Record<string, YardCreditPool[]>> {
    const res = await api.get('/driver-yard-credits/balances');
    const rows: Array<YardCreditPool & { driverId: number }> = res.data || [];
    return rows.reduce<Record<string, YardCreditPool[]>>((acc, row) => {
      const key = String(row.driverId);
      (acc[key] ||= []).push({
        yardId: row.yardId,
        yardName: row.yardName,
        yardFullName: row.yardFullName,
        colorIndex: row.colorIndex,
        balance: row.balance,
      });
      return acc;
    }, {});
  },

  /** Switch a container to direct pay (skip yard credit → normal segment pricing). */
  async directPay(containerId: number): Promise<{ containerId: number; repriced: Array<{ taskId: number; action: string }> }> {
    const res = await api.post(`/driver-yard-credits/container/${containerId}/direct-pay`);
    return res.data;
  },
};

export default driverYardCreditApi;
